import React, { useEffect, useMemo, useState } from "react";
import {
  AssignmentOutlined,
  ErrorOutline,
  EventOutlined,
  Refresh,
  SchoolOutlined,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./student-home-work.css";

const getAdmissionId = (child) =>
  child?.admission_id || child?.student?.admission_id || null;

function normalizeDateKey(value) {
  if (!value) return "";
  const text = String(value);
  const match = text.match(/\d{4}-\d{2}-\d{2}/);
  if (match) return match[0];
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toISOString().slice(0, 10);
}

function normalizeHomeWork(result) {
  const payload = result?.payload || result?.data || result;
  const records = Array.isArray(payload)
    ? payload
    : payload?.homework || payload?.home_work || [];

  if (!Array.isArray(records)) return [];

  const groups = records.reduce((itemsByDate, item) => {
    if (Array.isArray(item?.homework)) {
      const date = normalizeDateKey(item.date || item.homework[0]?.date || "");
      if (date) {
        itemsByDate[date] = [
          ...(itemsByDate[date] || []),
          ...item.homework.map((homeworkItem) => ({
            ...homeworkItem,
            date: normalizeDateKey(homeworkItem.date) || date,
          })),
        ];
      }
      return itemsByDate;
    }

    const date = normalizeDateKey(item?.date);
    if (date) {
      itemsByDate[date] = [...(itemsByDate[date] || []), { ...item, date }];
    }
    return itemsByDate;
  }, {});

  return Object.keys(groups)
    .sort((a, b) => String(b).localeCompare(String(a)))
    .map((date) => ({ date, homework: groups[date] }));
}

function formatDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-PK", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

const descriptionTags = new Set([
  "a",
  "b",
  "blockquote",
  "br",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "i",
  "li",
  "ol",
  "p",
  "span",
  "strong",
  "u",
  "ul",
]);
const blockedDescriptionTags = new Set([
  "embed",
  "iframe",
  "object",
  "script",
  "style",
  "svg",
]);

function renderDescriptionNode(node, key) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const tagName = node.tagName.toLowerCase();
  if (blockedDescriptionTags.has(tagName)) return null;

  const children = Array.from(node.childNodes).map((child, index) =>
    renderDescriptionNode(child, `${key}-${index}`),
  );

  if (!descriptionTags.has(tagName)) {
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }

  const props = { key };
  const textAlign = node.style?.textAlign;
  if (["left", "right", "center", "justify"].includes(textAlign)) {
    props.style = { textAlign };
  }

  if (tagName === "a") {
    const href = node.getAttribute("href") || "";
    if (/^(https?:|mailto:|tel:)/i.test(href)) {
      props.href = href;
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    }
  }

  if (tagName === "br") return React.createElement(tagName, props);
  return React.createElement(tagName, props, children);
}

function HomeworkDescription({ value, emptyText }) {
  if (!value) return <p>{emptyText}</p>;

  const documentBody = new DOMParser().parseFromString(value, "text/html").body;
  const content = Array.from(documentBody.childNodes).map((node, index) =>
    renderDescriptionNode(node, `description-${index}`),
  );

  return <div className="parent-home-work-description">{content}</div>;
}

function subjectInfo(item) {
  const subjectId = item?.subject_id ?? item?.subject?.id;
  const subjectName =
    item?.subject?.name || item?.subject_name || item?.subject || "";
  const subjectSlug = item?.subject?.slug || item?.subject_slug || "";
  const isGeneral =
    subjectId == null ||
    subjectId === "" ||
    String(subjectId).toLowerCase() === "general" ||
    String(subjectName).toLowerCase() === "general";

  if (isGeneral) {
    return { code: "GENERAL", name: "General" };
  }

  return {
    code: subjectSlug || subjectName || `Subject ${subjectId || ""}`.trim(),
    name: subjectName || subjectSlug || `Subject ${subjectId || ""}`.trim(),
  };
}

export default function StudentHomeWork() {
  const { selectedChild } = useParent();
  const [dateGroups, setDateGroups] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const admissionId = getAdmissionId(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";

  const loadHomeWork = async () => {
    if (!admissionId) {
      setDateGroups([]);
      setSelectedDate("");
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    setDateGroups([]);
    setSelectedDate("");

    try {
      const token = localStorage.getItem("parentToken");
      const response = await fetch(
        `${baseUrl}/student-home-work/${encodeURIComponent(admissionId)}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || "Unable to load student homework.");
      }

      const groups = normalizeHomeWork(result);
      setDateGroups(groups);
      setSelectedDate(groups[0]?.date || "");
    } catch (requestError) {
      setError(requestError.message || "Unable to load student homework.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHomeWork();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId]);

  const selectedGroup = useMemo(
    () => dateGroups.find((group) => group.date === selectedDate),
    [dateGroups, selectedDate],
  );
  const homeWork = selectedGroup?.homework || [];

  return (
    <section className="parent-progress-page parent-home-work-page">
      <div className="parent-progress-hero parent-home-work-hero">
        <div>
          <span className="parent-progress-eyebrow">Learning updates</span>
          <h1>Student Home Work</h1>
          <p>
            {selectedChild
              ? `Review subject homework assigned to ${studentName}.`
              : "Select a child to view assigned homework."}
          </p>
        </div>
        <label className="parent-home-work-date-filter">
          <span>Homework date</span>
          <input
            type="date"
            value={selectedDate}
            min={dateGroups[dateGroups.length - 1]?.date || undefined}
            max={dateGroups[0]?.date || undefined}
            onChange={(event) => setSelectedDate(event.target.value)}
            disabled={loading || !dateGroups.length}
          />
        </label>
      </div>

      {!admissionId ? (
        <PageState
          icon={SchoolOutlined}
          title="Student admission ID unavailable"
          text="Please select a child with a valid admission ID to view homework."
        />
      ) : loading ? (
        <PageState
          loading
          title="Loading homework"
          text="Getting the latest subject homework for this student."
        />
      ) : error ? (
        <PageState
          icon={ErrorOutline}
          error
          title="Homework could not be loaded"
          text={error}
          onRetry={loadHomeWork}
        />
      ) : !dateGroups.length ? (
        <PageState
          icon={AssignmentOutlined}
          title="No homework available"
          text="The school has not assigned homework for this student yet."
        />
      ) : (
        <div className="parent-home-work-content">
          <div className="parent-home-work-heading">
            <div>
              <h2>{formatDate(selectedDate)}</h2>
              <p>All subject homework assigned on the selected date.</p>
            </div>
            <span>
              {homeWork.length} subject{homeWork.length === 1 ? "" : "s"}
            </span>
          </div>

          {homeWork.length ? (
            <div className="parent-home-work-grid">
              {homeWork.map((item, index) => {
                const subject = subjectInfo(item);
                return (
                  <article
                    className="parent-home-work-card"
                    key={item.id || `${item.subject_id}-${index}`}
                  >
                    <div className="parent-home-work-card-top">
                      <span className="parent-home-work-icon">
                        <AssignmentOutlined />
                      </span>
                      <div>
                        <span className="parent-home-work-subject-code">
                          {subject.code}
                        </span>
                        <h3>{subject.name}</h3>
                      </div>
                    </div>
                    <div className="parent-home-work-card-body">
                      <h4>{item.title || "Home Work"}</h4>
                      <HomeworkDescription
                        value={item.description}
                        emptyText="No homework description was provided."
                      />
                      {item.remarks && (
                        <div className="parent-home-work-remarks">
                          <strong>Remarks</strong>
                          <HomeworkDescription
                            value={item.remarks}
                            emptyText="No remarks were provided."
                          />
                        </div>
                      )}
                    </div>
                    <div className="parent-home-work-card-date">
                      <EventOutlined /> {formatDate(item.date || selectedDate)}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="parent-home-work-empty-date" role="status">
              <AssignmentOutlined />
              <h3>No homework on this date</h3>
              <p>Choose another date to view assigned subject homework.</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PageState({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div
      className={`parent-home-work-state${error ? " error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {loading ? (
        <span className="parent-home-work-spinner" />
      ) : (
        Icon && <Icon />
      )}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh /> Try again
        </button>
      )}
    </div>
  );
}
